import { CommonModule, formatDate } from '@angular/common';
import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Observable, Subject, catchError, debounceTime, map, of, switchMap, tap } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { TabsScrollDirective } from '@shared/directives/tabs-scroll.directive';
import { FormacaoPrecosNFeEntradaErrorCodeEnum } from '@shared/enums/orquestrador-compras/formacao-precos-nfe-entrada-error-code.enum';
import { ImpostoRecuperavelEnum } from '@shared/enums/suprimentos/imposto-recuperavel.enum';
import { TipoBasePrecoVendaEnum } from '@shared/enums/suprimentos/tipo-base-preco-venda.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { MaskService } from '@shared/services/mask.service';
import { NFeEntradaFormacaoPrecosPreviaRequest } from '@shared/services/requests/orquestrador-compras/nfe-entrada-formacao-precos-previa.request';
import { NFeEntradaFormacaoPrecosAplicarRequest } from '@shared/services/requests/orquestrador-compras/nfe-entrada-formacao-precos-aplicar.request';
import { NFeEntradaFormacaoPrecosItemPreviaRequest } from '@shared/services/requests/orquestrador-compras/nfe-entrada-formacao-precos-item-previa.request';
import { NFeEntradaFormacaoPrecosImpostoRecuperavelStruct } from '@shared/services/structs/orquestrador-compras/nfe-entrada-formacao-precos-imposto-recuperavel.struct';
import { NFeEntradaFormacaoPrecosItemResultadoStruct } from '@shared/services/structs/orquestrador-compras/nfe-entrada-formacao-precos-item-resultado.struct';
import { NFeEntradaFormacaoPrecosItemStruct } from '@shared/services/structs/orquestrador-compras/nfe-entrada-formacao-precos-item.struct';
import { NFeEntradaFormacaoPrecosPreviaResponse } from '@shared/services/responses/orquestrador-compras/nfe-entrada-formacao-precos-previa.response';
import { NFeEntradaFormacaoPrecosResolucaoResponse } from '@shared/services/responses/orquestrador-compras/nfe-entrada-formacao-precos-resolucao.response';
import { FormacaoPrecosNotaFiscalEntradaDialogData } from './formacao-precos-nota-fiscal-entrada-dialog-data';
import { FormacaoPrecosMemoriaCalculoDialogComponent } from '../formacao-precos-memoria-calculo-dialog/formacao-precos-memoria-calculo-dialog.component';
import { FormacaoPrecosMemoriaCalculoDialogData } from '../formacao-precos-memoria-calculo-dialog/formacao-precos-memoria-calculo-dialog-data';

/** Linha editável da formação de preços: o item da nota, as escolhas da tela e o último resultado calculado. */
interface LinhaFormacaoPrecos {
  item: NFeEntradaFormacaoPrecosItemStruct;
  isSelecionado: boolean;
  valorDifal: number | null;
  /** Nulo usa o markup do produto ou o padrão da configuração (definido no servidor). */
  percentualMarkup: number | null;
  /** Preço exibido enquanto o campo está em foco. */
  precoDigitado: number | null;
  /** Preço digitado enviado ao servidor, que devolve o markup; sai quando o markup é editado. */
  precoVendaInformado: number | null;
  resultado: NFeEntradaFormacaoPrecosItemResultadoStruct | null;
}

const CHAVE_IMPOSTOS_RECUPERAVEIS = 'compra.item.v_impostos_recup';

/**
 * Formação de preços de uma NF-e lançada. Custo, impostos recuperáveis e preço de venda vêm sempre do
 * motor de custo e venda da API Suprimentos, pelas fórmulas configuradas para a empresa; a tela só
 * informa as escolhas (frete, DIFAL, impostos recuperáveis, base e markup) e mostra o resultado.
 */
const CHAVE_VARIAVEL_PRECO_CUSTO = 'compra.item.v_preco_custo';

@Component({
  selector: 'app-formacao-precos-nota-fiscal-entrada-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatButtonToggleModule, MatCheckboxModule,
    MatFormFieldModule, MatIconModule, MatInputModule, MatProgressBarModule, MatProgressSpinnerModule, MatSlideToggleModule, MatTableModule,
    MatTabsModule, MatTooltipModule, NgxCurrencyDirective, AlertCardComponent, TabsScrollDirective, AbntPipe],
  templateUrl: './formacao-precos-nota-fiscal-entrada-dialog.component.html',
  styleUrls: ['./formacao-precos-nota-fiscal-entrada-dialog.component.css'],
})
export class FormacaoPrecosNotaFiscalEntradaDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly recalculo$ = new Subject<void>();
  private readonly cachePrevia = new Map<string, NFeEntradaFormacaoPrecosPreviaResponse>();

  readonly TipoBasePrecoVendaEnum = TipoBasePrecoVendaEnum;
  readonly currencyMask = this.maskService.getDynamicCurrencyMask();
  readonly decimalMask = this.maskService.getDecimalMask2();
  readonly colunasCusto = ['selecao', 'item', 'precoUnitarioCompra', 'difal', 'precoCusto', 'custoMedio'];
  readonly colunasVenda = ['item', 'custoNovo', 'markup', 'precoVenda', 'precoAtual'];

  isLoading = true;
  isCalculando = false;
  isPreviaPendente = true;
  isAplicando = false;
  erroCarregamento: string | null = null;
  resolucao: NFeEntradaFormacaoPrecosResolucaoResponse | null = null;
  previa: NFeEntradaFormacaoPrecosPreviaResponse | null = null;
  linhas: LinhaFormacaoPrecos[] = [];
  valorFrete: number | null = 0;
  impostosSelecionados = new Set<ImpostoRecuperavelEnum>();
  idTipoBasePrecoVenda = TipoBasePrecoVendaEnum.PrecoCustoCompra;
  markupGeral: number | null = null;
  isAtualizarCusto = true;
  isAtualizarPrecoVenda = true;
  isAtualizarMarkup = false;

  constructor(
    private alertService: AlertService,
    private maskService: MaskService,
    private orquestradorService: NotaFiscalEntradaService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<FormacaoPrecosNotaFiscalEntradaDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: FormacaoPrecosNotaFiscalEntradaDialogData) { }

  ngOnInit(): void {
    this.recalculo$.pipe(
      tap(() => this.isPreviaPendente = true),
      debounceTime(300),
      map(() => this.montarRequestPrevia()),
      switchMap(request => this.obterPrevia(request)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(response => this.aplicarPrevia(response));
    this.carregar();
  }

  get titulo(): string {
    return `NF-e ${this.data.numeroNota} · ${this.data.nomeFornecedor}`;
  }

  get isTodosSelecionados(): boolean {
    return this.linhas.length > 0 && this.linhas.every(linha => linha.isSelecionado);
  }

  get isSelecaoParcial(): boolean {
    return this.linhas.some(linha => linha.isSelecionado) && !this.isTodosSelecionados;
  }

  get motivoAtualizarDesabilitado(): string {
    if (!this.isAtualizarCusto && !this.isAtualizarPrecoVenda && !this.isAtualizarMarkup)
      return 'Ative ao menos uma opção de atualização para atualizar os preços.';
    if (!this.linhas.some(linha => linha.isSelecionado)) return 'Selecione ao menos um item para atualizar os preços.';
    return '';
  }

  get motivoBaseDesabilitada(): string {
    return this.resolucao?.isFormulaPrecoVendaPersonalizada
      ? 'A fórmula de preço de venda da empresa é personalizada e já define a base do cálculo.'
      : '';
  }

  get motivoPrecoVendaDesabilitado(): string {
    return this.resolucao && !this.resolucao.isListaPrecoPadraoManual
      ? 'A lista de preço padrão calcula os preços automaticamente e não aceita preço informado.'
      : '';
  }

  carregar(): void {
    this.isLoading = true;
    this.erroCarregamento = null;
    this.orquestradorService.resolverFormacaoPrecos(this.data.idNFeEntrada).subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError) {
          this.erroCarregamento = response.errorDescription || 'Não foi possível abrir a formação de preços.';
          return;
        }
        this.resolucao = response;
        this.valorFrete = response.totais.valorFrete;
        this.idTipoBasePrecoVenda = response.idTipoBasePrecoVenda;
        this.impostosSelecionados = new Set(response.impostosRecuperaveis
          .filter(imposto => imposto.isPresenteNaFormula)
          .map(imposto => imposto.idImpostoRecuperavel));
        this.isAtualizarPrecoVenda = response.isListaPrecoPadraoManual;
        this.linhas = response.itens.map(item => ({
          item,
          isSelecionado: true,
          valorDifal: item.valorDifal,
          percentualMarkup: item.percentualMarkup,
          precoDigitado: null,
          precoVendaInformado: null,
          resultado: null,
        }));
        this.agendarRecalculo();
      },
      error: () => {
        this.isLoading = false;
        this.erroCarregamento = 'Não foi possível abrir a formação de preços.';
      },
    });
  }

  agendarRecalculo(): void {
    if (!this.resolucao) return;
    this.recalculo$.next();
  }

  isImpostoSelecionado(imposto: NFeEntradaFormacaoPrecosImpostoRecuperavelStruct): boolean {
    return this.impostosSelecionados.has(imposto.idImpostoRecuperavel);
  }

  motivoImpostoDesabilitado(imposto: NFeEntradaFormacaoPrecosImpostoRecuperavelStruct): string {
    if (!imposto.isPresenteNaFormula) return 'Este imposto não está na fórmula de impostos recuperáveis.';
    if (!this.resolucao?.isImpostoRecuperavelSelecionavel)
      return 'A fórmula de impostos recuperáveis não permite personalização; os impostos seguem a configuração da empresa.';
    return '';
  }

  alternarImposto(imposto: NFeEntradaFormacaoPrecosImpostoRecuperavelStruct): void {
    if (this.motivoImpostoDesabilitado(imposto)) return;
    if (this.impostosSelecionados.has(imposto.idImpostoRecuperavel)) this.impostosSelecionados.delete(imposto.idImpostoRecuperavel);
    else this.impostosSelecionados.add(imposto.idImpostoRecuperavel);
    this.agendarRecalculo();
  }

  alternarTodos(): void {
    const selecionar = !this.isTodosSelecionados;
    this.linhas.forEach(linha => linha.isSelecionado = selecionar);
  }

  alterarBase(idTipoBasePrecoVenda: TipoBasePrecoVendaEnum): void {
    this.idTipoBasePrecoVenda = idTipoBasePrecoVenda;
    this.agendarRecalculo();
  }

  aplicarMarkupATodos(): void {
    if (this.markupGeral === null) return;
    this.linhas.forEach(linha => {
      linha.percentualMarkup = this.markupGeral;
      linha.precoVendaInformado = null;
    });
    this.agendarRecalculo();
  }

  /** Editar o markup descarta o preço informado: volta a valer o markup. */
  alterarMarkup(linha: LinhaFormacaoPrecos): void {
    linha.precoVendaInformado = null;
    this.agendarRecalculo();
  }

  /** Custo sobre o qual o markup incide, conforme a base escolhida; o custo médio é o devolvido pela prévia. */
  custoBase(linha: LinhaFormacaoPrecos): number {
    return this.idTipoBasePrecoVenda === TipoBasePrecoVendaEnum.CustoMedio
      ? linha.resultado?.valorCustoMedio ?? 0
      : linha.resultado?.valorPrecoCusto ?? 0;
  }

  precoExibido(linha: LinhaFormacaoPrecos): number | null {
    return linha.precoDigitado ?? linha.resultado?.valorPrecoVenda ?? null;
  }

  /** Editar o preço envia o preço ao servidor, que define o markup sobre a base do modelo em uso e o devolve. */
  alterarPreco(linha: LinhaFormacaoPrecos, preco: number | null): void {
    linha.precoDigitado = preco;
    if (preco === null) return;
    linha.precoVendaInformado = preco;
    this.agendarRecalculo();
  }

  encerrarEdicaoPreco(linha: LinhaFormacaoPrecos): void {
    linha.precoDigitado = null;
  }

  abrirMemoriaCalculo(linha: LinhaFormacaoPrecos): void {
    if (!this.resolucao || !linha.resultado) return;
    const data = new FormacaoPrecosMemoriaCalculoDialogData();
    data.descricaoItem = linha.item.descricao;
    data.formula = this.resolucao.formulas.find(formula => formula.chaveSaida === CHAVE_VARIAVEL_PRECO_CUSTO) ?? null;
    data.variaveis = this.resolucao.variaveis;
    data.memoriaPrecoCusto = linha.resultado.memoriaCalculo.find(memoria => memoria.chaveVariavel === CHAVE_VARIAVEL_PRECO_CUSTO) ?? null;
    data.memoriaImpostosRecuperaveis = linha.resultado.memoriaCalculo
      .find(memoria => memoria.chaveVariavel === CHAVE_IMPOSTOS_RECUPERAVEIS) ?? null;
    this.dialog.open(FormacaoPrecosMemoriaCalculoDialogComponent, { width: '760px', maxWidth: '96vw', maxHeight: '90vh', data });
  }

  /** Nota com a formação já aplicada: só envia depois de confirmar que a aplicação anterior será substituída. */
  atualizarPrecos(): void {
    if (this.isAplicando || this.isPreviaPendente || this.isCalculando || this.motivoAtualizarDesabilitado) return;
    const ultimaAplicacao = this.resolucao?.datetimeUltimaAplicacao;
    if (!ultimaAplicacao) {
      this.enviarAplicacao(false);
      return;
    }
    this.confirmarSubstituicao(`A formação de preços desta nota já foi aplicada em ${formatDate(ultimaAplicacao, 'dd/MM/yyyy', 'pt-BR')}. `
      + 'Aplicar de novo substitui os custos e preços gravados daquela aplicação.');
  }

  cancelar(): void {
    this.dialogRef.close(false);
  }

  private confirmarSubstituicao(descricao: string): void {
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Aplicar a formação de novo',
        description: descricao,
        buttonConfirm: 'Aplicar de novo',
        buttonClose: 'Cancelar',
      },
    }).afterClosed().subscribe(result => {
      if (result?.confirm) this.enviarAplicacao(true);
    });
  }

  private enviarAplicacao(isSubstituirAplicacaoAnterior: boolean): void {
    const request = Object.assign(new NFeEntradaFormacaoPrecosAplicarRequest(), this.montarRequestPrevia());
    request.identificadoresSelecionados = this.linhas.filter(linha => linha.isSelecionado).map(linha => linha.item.identificador);
    request.isAtualizarCusto = this.isAtualizarCusto;
    request.isAtualizarPrecoVenda = this.isAtualizarPrecoVenda;
    request.isAtualizarMarkup = this.isAtualizarMarkup;
    request.isSubstituirAplicacaoAnterior = isSubstituirAplicacaoAnterior;

    this.isAplicando = true;
    this.orquestradorService.aplicarFormacaoPrecos(request).subscribe({
      next: response => {
        this.isAplicando = false;
        // Outra aplicação gravada depois que a tela abriu: pede a confirmação com a data devolvida pelo servidor.
        if (response.isError && response.errorCode === FormacaoPrecosNFeEntradaErrorCodeEnum.AplicacaoAnteriorSemConfirmacao) {
          this.confirmarSubstituicao(response.errorDescription);
          return;
        }
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Preços atualizados conforme as opções selecionadas.', AlertType.success);
        this.dialogRef.close(true);
      },
      error: () => {
        this.isAplicando = false;
        this.alertService.show('Erro', 'Não foi possível atualizar os preços. Tente novamente.', AlertType.error);
      },
    });
  }

  private montarRequestPrevia(): NFeEntradaFormacaoPrecosPreviaRequest {
    const request = new NFeEntradaFormacaoPrecosPreviaRequest();
    request.idNFeEntrada = this.data.idNFeEntrada;
    request.valorFrete = this.valorFrete ?? 0;
    request.idsImpostoRecuperavel = [...this.impostosSelecionados].sort();
    request.idTipoBasePrecoVenda = this.idTipoBasePrecoVenda;
    request.itens = this.linhas.map(linha => Object.assign(new NFeEntradaFormacaoPrecosItemPreviaRequest(), {
      identificador: linha.item.identificador,
      valorDifal: linha.valorDifal ?? 0,
      percentualMarkup: linha.percentualMarkup,
      precoVendaInformado: linha.precoVendaInformado,
    }));
    return request;
  }

  /** Mesma escolha, mesmo resultado: a impressão do request evita recalcular o que já foi calculado. */
  private obterPrevia(request: NFeEntradaFormacaoPrecosPreviaRequest): Observable<NFeEntradaFormacaoPrecosPreviaResponse> {
    const impressao = JSON.stringify(request);
    const cache = this.cachePrevia.get(impressao);
    if (cache) return of(cache);
    this.isCalculando = true;
    return this.orquestradorService.calcularPreviaFormacaoPrecos(request).pipe(
      tap(response => {
        if (!response.isError) this.cachePrevia.set(impressao, response);
      }),
      // Um erro de rede não pode encerrar o fluxo de recálculo: vira resposta de erro e a tela segue editável.
      catchError(() => of(Object.assign(new NFeEntradaFormacaoPrecosPreviaResponse(), {
        isError: true,
        errorDescription: 'Não foi possível calcular a formação de preços. Tente novamente.',
      }))),
    );
  }

  private aplicarPrevia(response: NFeEntradaFormacaoPrecosPreviaResponse): void {
    this.isCalculando = false;
    if (response.isError) {
      this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
      return;
    }
    this.previa = response;
    const porIdentificador = new Map(response.itens.map(item => [item.identificador, item] as const));
    this.linhas.forEach(linha => {
      linha.resultado = porIdentificador.get(linha.item.identificador) ?? null;
      // Preço informado: mostra o markup que o servidor definiu (a edição do markup já descartou o preço).
      if (linha.precoVendaInformado !== null && linha.resultado?.percentualMarkup != null)
        linha.percentualMarkup = linha.resultado.percentualMarkup;
    });
    this.isPreviaPendente = false;
  }
}
